/**
 * Compact, editable "Game profile" strip for the Design-tab nav. Shows the
 * genre / players / input / art-style captured by the wizard and lets the user
 * tweak them later; each change is persisted via the parent (writeProjectProfile)
 * and also keeps feeding the AI prompts. Collapsed to a one-line summary by
 * default to keep the nav tidy.
 */
import type { ReactElement } from 'react';
import type { DesignProfile } from '@volley/crucible-ipc-types';
import { GENRE_OPTIONS, INPUT_METHODS, MAX_PLAYERS, genreLabel } from './lib/profile-options';
import { Tip } from './Tip';

export interface GameProfileProps {
  profile: DesignProfile | undefined;
  onChange: (patch: Partial<DesignProfile>) => void;
}

function summarise(p: DesignProfile | undefined): string {
  if (!p) return 'Not set';
  const parts: string[] = [];
  if (p.genre && p.genre !== 'default') parts.push(genreLabel(p.genre));
  if (p.players) parts.push(p.players.mode === 'single' ? 'Single' : `Multi ·${p.players.max}`);
  if (p.inputStyles?.length)
    parts.push(`${p.inputStyles.length} input${p.inputStyles.length === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'Not set';
}

export function GameProfile({ profile, onChange }: GameProfileProps): ReactElement {
  const players = profile?.players ?? { mode: 'multiplayer' as const, max: 4 };
  const inputStyles = profile?.inputStyles ?? [];

  const toggleInput = (m: string): void =>
    onChange({
      inputStyles: inputStyles.includes(m)
        ? inputStyles.filter((x) => x !== m)
        : [...inputStyles, m],
    });

  return (
    <details
      className="mt-4 rounded-lg border border-crucible-border bg-white/[0.02]"
      data-testid="game-profile"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between px-2.5 py-2 text-sm">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-crucible-muted">
          Game profile
        </span>
        <span className="truncate text-xs text-crucible-muted" data-testid="game-profile-summary">
          {summarise(profile)}
        </span>
      </summary>

      <div className="space-y-3 px-2.5 pb-3 pt-1">
        <div>
          <label className="mb-1 block text-[11px] text-crucible-muted">Genre</label>
          <select
            value={profile?.genre ?? 'default'}
            data-testid="game-profile-genre"
            onChange={(e) => onChange({ genre: e.target.value as DesignProfile['genre'] })}
            className="w-full rounded border border-crucible-border bg-crucible-bg p-1.5 text-xs text-crucible-fg outline-none"
          >
            {GENRE_OPTIONS.map((g) => (
              <option key={g.key} value={g.key}>
                {g.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-[11px] text-crucible-muted">Players</label>
          <div className="flex items-center gap-1.5">
            <div className="flex rounded border border-crucible-border p-0.5 text-[11px]">
              {(['single', 'multiplayer'] as const).map((m) => (
                <Tip key={m} label={m === 'single' ? 'Single player' : 'Multiplayer'} align="start">
                  <button
                    type="button"
                    onClick={() => onChange({ players: { ...players, mode: m } })}
                    className={`peer rounded px-1.5 py-0.5 ${
                      players.mode === m
                        ? 'bg-crucible-accent text-white'
                        : 'text-crucible-muted hover:text-crucible-fg'
                    }`}
                  >
                    {m === 'single' ? 'Single' : 'Multi'}
                  </button>
                </Tip>
              ))}
            </div>
            {players.mode === 'multiplayer' && (
              <select
                value={players.max}
                aria-label="Max players"
                onChange={(e) => onChange({ players: { ...players, max: Number(e.target.value) } })}
                className="rounded border border-crucible-border bg-crucible-bg p-1 text-[11px] text-crucible-fg outline-none"
              >
                {Array.from({ length: MAX_PLAYERS - 1 }, (_, i) => i + 2).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] text-crucible-muted">Input style</label>
          <div className="flex flex-wrap gap-1">
            {INPUT_METHODS.map((m) => {
              const on = inputStyles.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleInput(m)}
                  className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                    on
                      ? 'border-crucible-accent bg-crucible-accent/20 text-crucible-fg'
                      : 'border-crucible-border text-crucible-muted hover:text-crucible-fg'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] text-crucible-muted">Art style</label>
          <input
            value={profile?.artStyle ?? ''}
            data-testid="game-profile-art"
            placeholder="e.g. flat, bright, playful"
            onChange={(e) => onChange({ artStyle: e.target.value })}
            className="w-full rounded border border-crucible-border bg-transparent p-1.5 text-xs text-crucible-fg outline-none"
          />
        </div>
      </div>
    </details>
  );
}
